import ImageUrlResizer from './image-url-resizer';

/**
 * Convert image file to base64 string
 * @param imageFile
 */
export function imageToBase64(imageFile: File | Blob) {
  return new Promise((resolve) => {
    const fileReader: FileReader = new FileReader();

    fileReader.onload = (event: Event) => {
      const target: EventTarget & { result?: string } = event.target;

      resolve(target.result);
    };

    // convert to base64 string
    fileReader.readAsDataURL(imageFile);
  });
}

/**
 * Creates image file
 * https://github.com/ricardo-ch/react-easy-crop
 * https://codesandbox.io/s/y09komm059?file=/src/rotateImage.js
 * @param url
 */
export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.crossOrigin = 'Anonymous'; // To prevent "The canvas has been tainted by cross-origin data" error
    image.onload = () => resolve(image);
    image.onerror = (error) => reject(error);
    image.src = url;
  });

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {File} imageSrc - Image File url
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 * @param {number} rotation - optional rotation parameter
 */
export async function getCroppedImg(imageSrc, pixelCrop, rotation = 0): Promise<Blob> {
  const image: any = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const safeArea = Math.max(image.width, image.height);

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea;
  canvas.height = safeArea;

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  // draw rotated image and store data.
  ctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx.putImageData(data, 0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x, 0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y);

  // As a blob
  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      resolve(file);
    }, 'image/jpeg');
  });
}

/**
 * Blob to url
 * @param blob
 */
export function blobToUrl(blob: Blob) {
  return URL.createObjectURL(blob);
}

/**
 * Get image data
 */
export const getImageData = (image, resize: number = undefined) => {
  let imgWidthResized;
  let imgHeightResized;

  if (resize) {
    const scale = Math.min(resize / image.width, resize / image.height);

    imgWidthResized = image.width * scale;
    imgHeightResized = image.height * scale;
  }

  const canvas = document.createElement('canvas');

  canvas.width = imgWidthResized || image.width;
  canvas.height = imgHeightResized || image.height;
  const context = canvas.getContext('2d');

  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  return context.getImageData(0, 0, canvas.width, canvas.height);
};

/**
 * Rename image url
 */
export function resizeImageByURL(url: string, resize: string, replaceSize = 's300-c') {
  // For non Google images, use query params API
  if (url?.includes('images.')) {
    let format = 'jpeg';
    let size;
    let crop = true;

    // get size from resize param
    const sizeMatch = resize.match(/\d+/);

    if (sizeMatch && sizeMatch.length) {
      size = +sizeMatch.join('');
    }

    // for google API RW stands for webp format, so we'll use that here
    if (resize.includes('rw')) {
      format = 'webp';
    }

    if (!resize.includes('c')) {
      crop = false;
    }

    return ImageUrlResizer.resizeS3ImageByURL(url, size, format, crop);
  }

  return url?.replace(new RegExp(`${replaceSize}$`), resize);
}
